import { useMemo } from 'react';
import { faComment, faCommentSlash } from '@fortawesome/free-solid-svg-icons';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import BoxComment from '@/components/BoxComment';
import Comment from '@/components/Comment';
import CommentInputBox from '@/components/CommentInputBox';
import Icon from '@/components/general/Icon';
import WithoutAnswerTag from '@/components/general/WithoutAnswerTag';
import NumberAnswer from '@/components/NumberAnswer';
import PriorityItem from '@/components/PriorityItem';
import Reactions from '@/components/Reactions';
import TextAnswer from '@/components/TextAnswer';
import { AnswerTypes, Comment as CommentType, QuestionOption, TReaction } from '@/types/api';
import styles from './QuestionItem.styles';

export type QuestionItemComponentProps = {
  isShowingInput: boolean;
  isFinished?: boolean;
  withoutLabel?: boolean;
  isPriority?: boolean;
  handleShowInput: () => void;
  handlePostComment: (comment?: string) => void;
  label: string;
  answerType: AnswerTypes;
  answer?: string;
  answerId: string;
  comments: CommentType[];
  description?: string;
  refCommentInput?: React.MutableRefObject<any>;
  questionOptions?: QuestionOption[];
  reactions?: TReaction[];
  refetchCheckin: () => void;
};

const QuestionItemComponent = function ({
  isShowingInput,
  isFinished,
  withoutLabel,
  handleShowInput,
  handlePostComment,
  label,
  description,
  answerType,
  answer,
  answerId,
  comments,
  refCommentInput,
  questionOptions,
  reactions,
  refetchCheckin,
  isPriority,
}: QuestionItemComponentProps): JSX.Element {
  const hasComments = useMemo(() => comments.length > 0, [comments]);

  const renderAnswer = useMemo(() => {
    if (!answer)
      return <WithoutAnswerTag label={isPriority ? 'Without Priorities' : 'No Answer'} />;

    if (answerType === AnswerTypes.NUMBER) {
      return <NumberAnswer key={answerId} answer={answer} limit={questionOptions?.length} />;
    }

    if (answerType === AnswerTypes.PRIORITY) {
      return (
        <PriorityItem
          disablePriority
          hideCopyButton
          id={answerId}
          isPriorityFinished={!!isFinished}
          description={answer}
        />
      );
    }

    return <TextAnswer key={answerId} answer={answer} />;
  }, [answer, answerId, answerType, isFinished, isPriority, questionOptions?.length]);

  const renderComments = useMemo(() => {
    if (!hasComments) return null;

    return comments.map((comment) => (
      <Comment
        refetchCheckin={refetchCheckin}
        reactions={comment.reactions ?? []}
        key={comment.id}
        commentId={comment.id}
        comment={comment.text}
        userName={comment.user?.name}
        date={comment.created_at}
      />
    ));
  }, [comments, hasComments, refetchCheckin]);

  const tooltipMessage = useMemo(() => {
    if (isShowingInput) return 'Hide comment box';
    return 'Make a comment';
  }, [isShowingInput]);

  const commentIconToShow = useMemo(() => {
    if (isShowingInput) return faCommentSlash;
    return faComment;
  }, [isShowingInput]);

  return (
    <Box sx={styles.container}>
      {!withoutLabel && (
        <Typography
          variant="h6"
          color="primary"
          marginBottom={description ? 0 : 1}
          fontWeight={500}
          data-testid="question-label"
        >
          {label}
        </Typography>
      )}

      {description && (
        <Typography marginBottom={1} variant="subtitle1">
          {description}
        </Typography>
      )}

      <Box display="flex" alignItems="flex-start" justifyContent="space-between">
        <Box display="flex" flex={0.98}>
          {renderAnswer}
        </Box>

        <Box display="flex" alignItems="flex-start" flex={0.02}>
          <Tooltip title={tooltipMessage} placement="left">
            <IconButton onClick={handleShowInput} data-testid="new-comment-button">
              <Icon icon={commentIconToShow} color="primary" variantColor="dark" size={20} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Reactions refetchCheckin={refetchCheckin} reactions={reactions ?? []} answerId={answerId} />

      {isShowingInput && (
        <BoxComment>
          <CommentInputBox isFocused handlePostComment={handlePostComment} />
        </BoxComment>
      )}

      {!isShowingInput && hasComments && (
        <BoxComment>
          {renderComments}

          <CommentInputBox customRef={refCommentInput} handlePostComment={handlePostComment} />
        </BoxComment>
      )}
    </Box>
  );
};

export default QuestionItemComponent;
